import { cn } from 'cn'
import type { ReactNode } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog'

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  className?: string
  dataTour?: string
  /**
   * Drop the drag handle and the close button, for the sheets production draws
   * as a bare titled list. Tapping outside then closes the sheet — with both
   * visible affordances gone it would otherwise be a trap.
   */
  hideChrome?: boolean
  /**
   * When false, the inner wrapper does NOT scroll — children own the flex
   * layout and must set their own `overflow-y-auto` on the scrollable region.
   * Set this when the sheet needs a pinned footer (e.g. filter sheets with
   * sticky Apply/Clear actions): otherwise the outer scroll scrolls the
   * footer along with the content. Defaults to `true` to preserve existing
   * sheet behavior.
   */
  scrollable?: boolean
}

function BottomSheet({
  open,
  onClose,
  title,
  children,
  className,
  dataTour,
  hideChrome = false,
  scrollable = true,
}: Readonly<BottomSheetProps>) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-tour={dataTour}
        showCloseButton={!hideChrome}
        className={cn(
          'fixed bottom-0 left-0 right-0 top-auto m-0 max-h-[85dvh] w-full max-w-none',
          'flex flex-col translate-x-0 translate-y-0 rounded-t-xl px-6 pb-8 pt-4',
          'data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full',
          className,
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => {
          if ((e.target as Element | null)?.closest('[data-tour-overlay]')) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          // A coach mark sits outside the sheet; interacting with it must not
          // dismiss the sheet it is describing.
          if ((e.target as Element | null)?.closest('[data-tour-overlay]')) {
            e.preventDefault()
            return
          }
          if (hideChrome) onClose()
          else e.preventDefault()
        }}
      >
        {!hideChrome && <div className="mx-auto mb-4 h-1 w-10 shrink-0 rounded-full bg-border" />}
        {title && (
          <DialogHeader className="mb-4 shrink-0">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        <div className={cn('flex min-h-0 flex-1 flex-col', scrollable && 'overflow-y-auto')}>
          {children}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { BottomSheet }
